/**
 * **Notes** — the customer's, the office's and the crew's, as one block
 * (board cb-07): a **Notes** heading, a `Customer · Internal · Crew` switch,
 * one 56px box under it and the line saying where that note goes. Props only.
 *
 * It was three labelled textareas. The three notes are still three values and
 * the props are unchanged — the switch only chooses which one the box edits,
 * so nothing typed under one audience is lost by looking at another.
 *
 * A visit's block first, and a service's since Aaron's review item 3
 * (migration 0125): the catalog service and the quote's service carry the
 * same three notes, so they wear the same block rather than a copy of it.
 * What differs is said by props — `hints`, because "under this visit" is
 * wrong under a service, and `ruled`, because a popover's sections are
 * separated by a hairline and the catalog sheet's are not.
 */
import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { EditorTextArea } from '../editor-shell.js'
import {
  NOTE_ARIA, NOTE_HINTS, NOTE_TABS, NOTES_SECTION, USE_CATALOG_WORDS, type NoteAudience,
} from '../copy.js'

const AUDIENCES: NoteAudience[] = ['quote', 'internal', 'crew']

export function NotesField({
  quote, internal, crew, onQuote, onInternal, onCrew, hints = NOTE_HINTS, ruled = true, follows,
}: {
  /** The customer's note — a visit's `noteQuote`, a service's `noteCustomer` or catalog `description`. */
  quote: string
  internal: string
  crew: string
  onQuote: (value: string) => void
  onInternal: (value: string) => void
  onCrew: (value: string) => void
  /** The line under the box per audience; the visit's words unless the caller says otherwise. */
  hints?: Record<NoteAudience, string | null>
  /** The hairline above the heading, which a popover's sections wear. */
  ruled?: boolean
  /**
   * The words each note follows when it says nothing of its own — a
   * package's service follows the catalog service (0127). Given, a note that
   * no longer reads the same offers **Use the catalog's words** under the box
   * (step 25), which puts them back; the caller stores matching words as
   * "follows", so the note goes on tracking the catalog from then on.
   */
  follows?: Record<NoteAudience, string>
}) {
  /* cb-07 opens on Customer. */
  const [audience, setAudience] = useState<NoteAudience>('quote')
  const value = { quote, internal, crew }[audience]
  const onChange = { quote: onQuote, internal: onInternal, crew: onCrew }[audience]
  const hint = hints[audience]
  const followed = follows?.[audience]
  const differs = followed !== undefined && value.trim() !== followed.trim()

  return (
    /* The Billing and Options sections' own shell, so the popover's sections are one family. */
    <section data-notes-field=""
      className={cn('flex flex-col gap-2', ruled && 'border-t border-line-soft pt-4')}>
      <b className="text-[14px] font-bold text-text">{NOTES_SECTION}</b>
      {/*
        * cb-07: a full-width track, 2px inset and 2px apart, radius 9; each
        * segment 26px tall, radius 7, 12px — the chosen one white at 600 with
        * a hairline and the segmented shadow, the others muted at 500.
        *
        * **The three segments share the track equally** (Aaron's review item
        * 3): cb-07 drew them compact at the left, which left two-thirds of a
        * full-width bar as empty track that looked like a fourth, blank
        * choice. `flex-1` with `min-w-0` splits it in three whatever the
        * popover's width.
        */}
      <div role="tablist" aria-label={NOTES_SECTION}
        className="flex gap-0.5 rounded-[9px] bg-segmented-track p-0.5">
        {AUDIENCES.map((one) => (
          <button
            key={one}
            type="button"
            role="tab"
            aria-selected={audience === one}
            onClick={() => setAudience(one)}
            className={cn(
              'inline-flex h-[26px] min-w-0 flex-1 items-center justify-center whitespace-nowrap rounded-[7px] px-3 text-[12px]',
              audience === one
                ? 'bg-surface font-semibold text-text shadow-segmented ring-1 ring-line'
                : 'font-medium text-text-muted hover:text-text',
            )}
          >
            {NOTE_TABS[one]}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={NOTE_TABS[audience]} className="flex flex-col">
        <EditorTextArea
          value={value}
          rows={2}
          aria-label={NOTE_ARIA[audience]}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-[56px]"
        />
      </div>
      {hint && <span className="-mt-1 text-[11px] leading-[1.4] text-text-faint">{hint}</span>}
      {differs && (
        <button
          type="button"
          data-use-catalog=""
          onClick={() => onChange(followed)}
          className="-mt-0.5 self-start text-[12px] font-semibold text-accent hover:text-accent-dark"
        >
          {USE_CATALOG_WORDS}
        </button>
      )}
    </section>
  )
}
